import { useMemo, type RefObject } from "react"

import { Alert, Grid } from "@mui/material"
import { useTranslation } from "react-i18next"

import ProductCard from "@/components/ProductCard"
import { useDetailCompleteEvent } from "@/features/DataLayer"
import { usePrices } from "@/queries/hooks"
import { CartUsers, Product, ProductType } from "@/queries/models"

import { useCartUserAutoSelect, type ProductStatusInfo } from "../hooks"
import {
  matchPricesWithAgeGroups,
  getStatusOverlayText,
  getStatusBadgeText,
} from "../utils"

type ProductListProps = {
  arrivalDate: Date
  products: Product[]
  productIds: string[]
  productTypeId: ProductType
  categoryId: number
  bookingLengthName?: string
  cartUsers?: CartUsers
  hasAutoSelected?: RefObject<boolean>
  getProductStatus: (productId: string) => ProductStatusInfo | undefined
  isProductSelectable: (productId: string) => boolean
  isStatusLoading: boolean
}

const ProductList = ({
  products,
  productIds,
  arrivalDate,
  productTypeId,
  categoryId,
  bookingLengthName,
  cartUsers,
  hasAutoSelected,
  getProductStatus,
  isProductSelectable,
  isStatusLoading,
}: ProductListProps) => {
  const { t } = useTranslation()
  const runDetailComplete = useDetailCompleteEvent()

  const {
    data: priceResponse,
    isLoading: isPricesLoading,
    error: pricesError,
  } = usePrices({
    arrivalDate,
    productIds,
    productTypeId,
    categoryId,
  })

  const productsWithPrices = useMemo(
    () => matchPricesWithAgeGroups(products, priceResponse),
    [products, priceResponse]
  )

  useCartUserAutoSelect(
    productsWithPrices,
    cartUsers,
    isPricesLoading,
    hasAutoSelected,
    isProductSelectable
  )

  return (
    <>
      {pricesError && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{t("priceFetchError")}</Alert>
        </Grid>
      )}
      {productsWithPrices.map((product) => {
        const statusInfo = getProductStatus(product.id)
        const statusOverlayText = getStatusOverlayText(statusInfo)
        const statusActionNoteText = getStatusBadgeText(statusInfo)

        return (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={product.id}>
            <ProductCard
              productName={product.name}
              description={product.description}
              additionalDescription={product.additionalDescription}
              imageId={product.imageId}
              memberInformation={product.memberInformation!}
              ageGroups={product.ageGroups}
              productId={product.id}
              idImageRequired={product.idImageRequired}
              isLoading={isPricesLoading || isStatusLoading}
              isSubscriptionProduct={product.isSubscriptionProduct}
              personCertificateRequired={product.personCertificateRequired}
              disabledMessage={statusOverlayText}
              actionNote={statusActionNoteText}
              bookingLengthInDays={product.bookingLength}
              bookingLengthName={bookingLengthName}
              dealBadge={product.dealBadge}
              showFreeSkiDays={product.showFreeSkiDays}
              subscriptionConfigurationId={product.subscriptionConfigurationId}
              category={
                productTypeId === ProductType.Ticket ? "Ticket" : "SkiPass"
              }
              resortId={product.resortId}
              capacityGroupId={product.capacityGroupId}
              onDetailComplete={runDetailComplete}
            />
          </Grid>
        )
      })}
    </>
  )
}

export default ProductList
